import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { Box, Text, Flex, Image, Icon } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FaLongArrowAltRight } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { eventTypeColors } from "../../shared-utils/values";
import { format } from "date-fns";
const DEFAULT_PRIMARY = "#8155FE";
const DEFAULT_TEXT = "white";
const UpcomingEventsCarousel = ({ events, splitLayout = false, _pubId, pubColors, onEventClick, appContext = "customer", }) => {
    var _a, _b;
    const primaryColor = (_a = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primary) !== null && _a !== void 0 ? _a : DEFAULT_PRIMARY;
    const textColor = (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _b !== void 0 ? _b : DEFAULT_TEXT;
    const navigate = useNavigate();
    const { pubId: urlPubId } = useParams();
    const pubId = urlPubId || _pubId;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const imagesStackRef = useRef(null);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const touchStartTime = useRef(0);
    const isSwipe = useRef(false);
    const touchMoved = useRef(false);
    const clickAllowed = useRef(true);
    const transitionTimeoutRef = useRef(null);
    const isProgrammaticTransition = useRef(false);
    const totalCards = events.length;
    const minSwipeDistance = 50;
    // Update card positions in stack
    const updateCardStack = (force = false, direction = null, targetIndex = null) => {
        var _a;
        if (isTransitioning && !force)
            return;
        const cards = (_a = imagesStackRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll("[data-card-index]");
        if (!cards || cards.length === 0)
            return;
        // Use targetIndex if provided, otherwise use currentIndex
        const indexToUse = targetIndex !== null ? targetIndex : currentIndex;
        cards.forEach((card, index) => {
            const cardIndex = parseInt(card.getAttribute("data-card-index") || "0");
            let stackIndex = (cardIndex - indexToUse + totalCards) % totalCards;
            // Remove animation classes
            card.classList.remove("active", "swiping-left", "swiping-right", "dragging");
            // Add swipe animation classes based on direction
            if (direction && stackIndex === 0) {
                card.classList.add(direction === "next" ? "swiping-left" : "swiping-right");
            }
            // Set z-index and transform based on stack position
            if (stackIndex === 0) {
                card.classList.add("active");
                card.style.zIndex = `${totalCards + 1}`;
                card.style.transform =
                    "translate(-50%, -50%) translateX(0) scale(1) rotateY(0deg)";
                card.style.opacity = "1";
                card.style.filter = "brightness(1)";
                card.style.pointerEvents = "auto";
            }
            else if (stackIndex <= 6) {
                card.style.zIndex = `${totalCards - stackIndex}`;
                card.style.pointerEvents = "none";
                // Improved stacking with smoother spacing and better visual trail
                const offsetX = 12 * stackIndex + stackIndex * stackIndex * 1.5;
                const offsetY = -4 * stackIndex;
                const scale = Math.max(0.75, 1 - stackIndex * 0.05);
                const rotateY = stackIndex * 1.5;
                const brightness = Math.max(0.75, 1 - stackIndex * 0.08);
                const opacity = Math.max(0.4, 1 - stackIndex * 0.12);
                card.style.transform = `translate(-50%, -50%) translateX(${offsetX}px) translateY(${offsetY}px) scale(${scale}) rotateY(${rotateY}deg)`;
                card.style.opacity = opacity.toString();
                card.style.filter = `brightness(${brightness})`;
            }
            else {
                card.style.zIndex = "0";
                card.style.opacity = "0";
                card.style.pointerEvents = "none";
                card.style.transform = `translate(-50%, -50%) translateX(250px) scale(0.6)`;
            }
        });
    };
    // Update text content
    const updateTextContent = () => {
        const contentItems = document.querySelectorAll("[data-content-index]");
        contentItems.forEach((item, index) => {
            if (index === currentIndex) {
                item.classList.add("active");
            }
            else {
                item.classList.remove("active");
            }
        });
    };
    // Cancel any ongoing transition smoothly
    const cancelTransition = () => {
        var _a;
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
            transitionTimeoutRef.current = null;
        }
        setIsTransitioning(false);
        isProgrammaticTransition.current = false;
        const cards = (_a = imagesStackRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll("[data-card-index]");
        cards === null || cards === void 0 ? void 0 : cards.forEach((card) => {
            // Remove animation classes but keep transitions for smooth animation
            card.classList.remove("swiping-left", "swiping-right", "dragging");
            // Ensure transition CSS property is present for smooth animation
            // Don't reset transform/opacity here - let them smoothly transition to new values
            if (card.style.transition === "none" || !card.style.transition) {
                card.style.transition = "";
            }
        });
        // Note: We don't call updateCardStack here because we want the new transition
        // to smoothly continue from current positions. The calling function will handle
        // the position update with proper animation direction.
    };
    // Go to previous card
    const prevCard = (force = false) => {
        // Always cancel any ongoing transition to allow immediate response
        if (isTransitioning) {
            cancelTransition();
            // Small delay to ensure smooth transition from cancelled state
            requestAnimationFrame(() => {
                proceedWithTransition("prev");
            });
        }
        else {
            proceedWithTransition("prev");
        }
    };
    // Proceed with transition after canceling any ongoing one
    const proceedWithTransition = (direction) => {
        setIsTransitioning(true);
        isProgrammaticTransition.current = true;
        // Calculate the new index
        const newIndex = direction === "next"
            ? (currentIndex + 1) % totalCards
            : (currentIndex - 1 + totalCards) % totalCards;
        // Update the index
        setCurrentIndex(newIndex);
        // Clear any existing timeout
        if (transitionTimeoutRef.current) {
            clearTimeout(transitionTimeoutRef.current);
        }
        // Use double requestAnimationFrame to ensure CSS transitions pick up the new state smoothly
        // Pass the new index directly to avoid closure issues
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                updateCardStack(true, direction, newIndex);
            });
        });
        transitionTimeoutRef.current = setTimeout(() => {
            var _a;
            setIsTransitioning(false);
            isProgrammaticTransition.current = false;
            const cards = (_a = imagesStackRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll("[data-card-index]");
            cards === null || cards === void 0 ? void 0 : cards.forEach((card) => {
                card.classList.remove("swiping-left", "swiping-right");
            });
            transitionTimeoutRef.current = null;
        }, 650);
    };
    // Go to next card
    const nextCard = (force = false) => {
        // Always cancel any ongoing transition to allow immediate response
        if (isTransitioning) {
            cancelTransition();
            // Small delay to ensure smooth transition from cancelled state
            requestAnimationFrame(() => {
                proceedWithTransition("next");
            });
        }
        else {
            proceedWithTransition("next");
        }
    };
    // Touch handlers
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
        touchStartTime.current = Date.now();
        isSwipe.current = false;
        touchMoved.current = false;
        clickAllowed.current = true;
    };
    const handleTouchMove = (e) => {
        var _a, _b, _c, _d;
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const deltaX = currentX - touchStartX.current;
        const deltaY = Math.abs(currentY - touchStartY.current);
        const absDeltaX = Math.abs(deltaX);
        if (absDeltaX > 5 || deltaY > 5) {
            touchMoved.current = true;
        }
        // Early detection: If vertical movement is clearly dominant, treat as scrolling
        if (deltaY > absDeltaX * 1.5 && deltaY > 15) {
            isSwipe.current = false;
            const activeCard = (_a = imagesStackRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(".active");
            if (activeCard) {
                activeCard.classList.remove("dragging", "swiping-left", "swiping-right");
                activeCard.style.transition = "none";
                activeCard.style.transform =
                    "translate(-50%, -50%) translateX(0) scale(1) rotateY(0deg)";
                activeCard.style.opacity = "1";
                setTimeout(() => {
                    if (activeCard) {
                        activeCard.style.transition = "";
                    }
                }, 50);
            }
            return;
        }
        // Only treat as swipe if horizontal movement is significantly greater than vertical
        const isHorizontalSwipe = absDeltaX > 10 && absDeltaX > deltaY * 1.5;
        if (isHorizontalSwipe) {
            isSwipe.current = true;
            const swipeProgress = deltaX / (((_b = imagesStackRef.current) === null || _b === void 0 ? void 0 : _b.offsetWidth) || 1);
            const activeCard = (_c = imagesStackRef.current) === null || _c === void 0 ? void 0 : _c.querySelector(".active");
            if (activeCard) {
                // If transitioning, cancel it first to allow immediate drag response
                if (isTransitioning) {
                    // Cancel transition smoothly - this will preserve current visual state
                    cancelTransition();
                }
                // Add dragging class and disable transitions for immediate response
                activeCard.classList.add("dragging");
                activeCard.style.transition = "none";
                // Apply drag transform immediately
                const translateX = deltaX * 0.3;
                const rotateY = deltaX * 0.05;
                const scale = 1 - Math.abs(swipeProgress) * 0.1;
                activeCard.style.transform = `translate(-50%, -50%) translateX(${translateX}px) scale(${scale}) rotateY(${rotateY}deg)`;
                activeCard.style.opacity = Math.max(0.7, 1 - Math.abs(swipeProgress) * 0.3).toString();
            }
        }
        else {
            isSwipe.current = false;
            const activeCard = (_d = imagesStackRef.current) === null || _d === void 0 ? void 0 : _d.querySelector(".active.dragging");
            if (activeCard) {
                activeCard.classList.remove("dragging");
                activeCard.style.transition = "none";
                activeCard.style.transform =
                    "translate(-50%, -50%) translateX(0) scale(1) rotateY(0deg)";
                activeCard.style.opacity = "1";
                setTimeout(() => {
                    if (activeCard) {
                        activeCard.style.transition = "";
                    }
                }, 50);
            }
        }
    };
    const handleTouchEnd = (e) => {
        var _a, _b;
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const swipeDistance = touchStartX.current - touchEndX;
        const verticalSwipe = Math.abs(touchStartY.current - touchEndY);
        const touchDuration = Date.now() - touchStartTime.current;
        const activeCard = (_a = imagesStackRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(".active");
        const absSwipeDistance = Math.abs(swipeDistance);
        const wasSwipe = isSwipe.current &&
            absSwipeDistance > minSwipeDistance &&
            absSwipeDistance > verticalSwipe * 1.5;
        const wasTap = !touchMoved.current ||
            (absSwipeDistance < 10 &&
                Math.abs(touchStartY.current - touchEndY) < 10 &&
                touchDuration < 300);
        const isVerticalScroll = verticalSwipe > absSwipeDistance && verticalSwipe > 20;
        if (isVerticalScroll) {
            if (activeCard) {
                activeCard.classList.remove("dragging", "swiping-left", "swiping-right");
                activeCard.style.transition = "none";
                activeCard.style.transform =
                    "translate(-50%, -50%) translateX(0) scale(1) rotateY(0deg)";
                activeCard.style.opacity = "1";
                setTimeout(() => {
                    if (activeCard) {
                        activeCard.style.transition = "";
                    }
                }, 50);
            }
            isSwipe.current = false;
            touchMoved.current = false;
            return;
        }
        if (wasSwipe) {
            clickAllowed.current = false;
            // Force the transition even if one is in progress
            // Don't clear styles here - let proceedWithTransition handle the positioning
            if (activeCard) {
                activeCard.classList.remove("dragging");
                // Reset transition to allow smooth animation
                activeCard.style.transition = "";
            }
            if (swipeDistance > 0) {
                nextCard(true);
            }
            else {
                prevCard(true);
            }
            setTimeout(() => {
                clickAllowed.current = true;
                touchMoved.current = false;
            }, 300);
        }
        else if (wasTap && clickAllowed.current && activeCard) {
            activeCard.classList.remove("dragging", "swiping-left", "swiping-right");
            activeCard.style.transform =
                "translate(-50%, -50%) translateX(0) scale(1) rotateY(0deg)";
            activeCard.style.opacity = "1";
            activeCard.style.transition = "none";
            setTimeout(() => {
                if (activeCard) {
                    activeCard.style.transition = "";
                }
            }, 50);
            clickAllowed.current = false;
            if (onEventClick) {
                onEventClick(events[currentIndex], currentIndex);
            }
            else if ((_b = events[currentIndex]) === null || _b === void 0 ? void 0 : _b.uuid) {
                const eventDate = formatDate(events[currentIndex]);
                const dateString = eventDate ? format(eventDate, "yyyy-MM-dd") : "";
                navigate(`/pub/detail/events/${pubId}?date=${dateString}&id=${events[currentIndex].uuid}`);
            }
            setTimeout(() => {
                clickAllowed.current = true;
            }, 500);
        }
        else if (activeCard) {
            // Reset card to original position when swipe is invalid
            activeCard.classList.remove("dragging", "swiping-left", "swiping-right");
            activeCard.style.transition =
                "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)";
            activeCard.style.transform =
                "translate(-50%, -50%) translateX(0) scale(1) rotateY(0deg)";
            activeCard.style.opacity = "1";
            activeCard.style.filter = "brightness(1)";
            setTimeout(() => {
                if (activeCard) {
                    activeCard.style.transition = "";
                }
            }, 300);
            touchMoved.current = false;
            isSwipe.current = false;
        }
    };
    // Handle card click (desktop)
    const handleCardClick = (index) => {
        var _a;
        if (clickAllowed.current && !isSwipe.current && !touchMoved.current) {
            clickAllowed.current = false;
            if (onEventClick) {
                onEventClick(events[index], index);
            }
            else if ((_a = events[index]) === null || _a === void 0 ? void 0 : _a.uuid) {
                const eventDate = formatDate(events[currentIndex]);
                const dateString = eventDate ? format(eventDate, "yyyy-MM-dd") : "";
                navigate(`/pub/detail/events/${pubId}?date=${dateString}&id=${events[currentIndex].uuid}`);
            }
            setTimeout(() => {
                clickAllowed.current = true;
            }, 500);
        }
    };
    // Format date
    const formatDate = (event) => {
        let date = null;
        if (event.event_date && event.event_date.length >= 3) {
            date = new Date(`${event.event_date[0]}/${event.event_date[1]}/${event.event_date[2]}`);
        }
        return date;
    };
    // Update cards when currentIndex changes
    useEffect(() => {
        // Skip update if we're in a programmatic transition (proceedWithTransition handles it)
        if (isProgrammaticTransition.current) {
            updateTextContent();
            return;
        }
        updateCardStack(true);
        updateTextContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, totalCards]);
    // Initialize cards on mount - ensure DOM is ready
    useEffect(() => {
        // Use requestAnimationFrame to ensure DOM is fully rendered and painted
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                updateCardStack(true);
                updateTextContent();
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (transitionTimeoutRef.current) {
                clearTimeout(transitionTimeoutRef.current);
            }
        };
    }, []);
    if (events.length === 0)
        return null;
    // Image Cards Stack Container
    const imageStack = (_jsx(Box, { ref: imagesStackRef, position: "relative", width: "100%", height: splitLayout ? "550px" : "400px", overflow: "visible", padding: "1rem 0", display: "flex", alignItems: "center", justifyContent: "center", pl: splitLayout ? 0 : 4, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, style: {
            touchAction: "pan-y pinch-zoom",
            outline: "none",
            WebkitTapHighlightColor: "transparent",
        }, children: _jsx(Box, { "data-carousel-image": true, position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "visible", children: events.map((event, index) => (_jsx(Box, { "data-card-index": index, position: "absolute", width: "85%", maxW: splitLayout ? "450px" : "380px", height: "auto", maxH: splitLayout ? "500px" : "380px", left: "50%", top: "50%", transformOrigin: "center center", cursor: "grab", userSelect: "none", opacity: 0, pointerEvents: "none", style: {
                    transformStyle: "preserve-3d",
                    willChange: "transform, opacity, filter",
                    transition: "transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    outline: "none",
                    WebkitTapHighlightColor: "transparent",
                }, onClick: () => handleCardClick(index), onMouseDown: (e) => {
                    if (e.button === 0) {
                        e.currentTarget.style.cursor = "grabbing";
                    }
                }, onMouseUp: (e) => {
                    e.currentTarget.style.cursor = "grab";
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.cursor = "grab";
                }, children: _jsx(Image, { src: event.image || "", alt: event.event_name || "Event", maxW: "100%", maxH: splitLayout ? "500px" : "380px", width: "auto", height: "auto", objectFit: "contain", borderRadius: "16px", boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)", display: "block", style: {
                        maxWidth: "100%",
                        maxHeight: splitLayout ? "500px" : "380px",
                        width: "auto",
                        height: "auto",
                        transform: "perspective(1000px) rotateY(-16deg)",
                        transformOrigin: "center center",
                        transition: "box-shadow 0.3s ease, transform 0.3s ease",
                        outline: "none",
                        WebkitTapHighlightColor: "transparent",
                    } }) }, event.uuid || index))) }) }));
    // Fixed Text Content
    const textContent = (_jsx(Box, { position: "relative", px: 0, minHeight: splitLayout ? "200px" : "115px", height: "fit-content", mb: splitLayout ? 4 : 0, pb: splitLayout ? 0 : "5rem", children: events.map((event, index) => {
            var _a;
            return (_jsxs(Box, { "data-content-index": index, position: splitLayout ? "relative" : "absolute", top: splitLayout ? "auto" : "0", left: splitLayout ? "auto" : "0", right: splitLayout ? "auto" : "0", padding: "1rem 1.5rem", height: "100%", maxWidth: "100%", opacity: index === currentIndex ? 1 : 0, transform: index === currentIndex
                    ? "translateY(0) scale(1)"
                    : "translateY(20px) scale(0.95)", style: {
                    transition: "opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    pointerEvents: index === currentIndex ? "auto" : "none",
                    display: splitLayout && index !== currentIndex ? "none" : "block",
                }, className: index === currentIndex ? "active" : "", children: [_jsx(Text, { fontSize: ["xs", "xs", "xs", "lg"], fontWeight: "400", bgGradient: eventTypeColors[event.event_type.toLowerCase()] ||
                            "linear-gradient(95.58deg, #A855F7 12.18%, #E879F9 86.93%)", bgClip: "text", color: "transparent", w: "fit-content", children: (_a = event.event_type) === null || _a === void 0 ? void 0 : _a.toUpperCase() }), _jsx(Box, { mt: [2, 2, 2, 4], mb: [4, 4, 4, 6], display: "flex", flexDir: ["row", "row", "row", "column"], gap: 2, children: _jsx(Flex, { align: "baseline", gap: 2, mb: 1, children: event.event_date && (_jsxs(_Fragment, { children: [_jsx(Text, { fontSize: ["2xl", "2xl", "2xl", "4xl"], fontWeight: "800", color: textColor, lineHeight: "1", children: format(formatDate(event) || new Date(), "d") }), _jsx(Text, { fontSize: ["xs", "xs", "xs", "lg"], fontWeight: "400", color: textColor, children: format(formatDate(event) || new Date(), "MMMM, EEE") })] })) }) }), _jsx(Text, { fontSize: ["lg", "lg", "lg", "2xl"], fontWeight: "700", color: textColor, lineHeight: "1.2", margin: "0.5rem 0", noOfLines: 2, wordBreak: "break-word", maxWidth: "100%", children: event.event_name || "Event" })] }, event.uuid || index));
        }) }));
    // Navigation Controls
    const navigationControls = (_jsxs(Flex, { position: "relative", padding: "1.5rem", alignItems: "center", justifyContent: [
            "space-between",
            "space-between",
            "space-between",
            "flex-start",
        ], gap: ["1rem", "1rem", "1rem", "3rem"], zIndex: 10, marginTop: "auto", children: [_jsxs(Flex, { gap: "0.75rem", children: [_jsx(Box, { as: "button", width: "36px", height: "36px", border: `1px solid ${primaryColor}`, background: "transparent", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.3s ease", color: primaryColor, onClick: () => prevCard(), _hover: { background: "rgba(129, 85, 254, 0.1)" }, _active: { transform: "scale(0.95)" }, "aria-label": "Previous", children: _jsx(ChevronLeftIcon, { boxSize: 5 }) }), _jsx(Box, { as: "button", width: "36px", height: "36px", border: `1px solid ${primaryColor}`, background: "transparent", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.3s ease", color: primaryColor, onClick: () => nextCard(), _hover: { background: "rgba(129, 85, 254, 0.1)" }, _active: { transform: "scale(0.95)" }, "aria-label": "Next", children: _jsx(ChevronRightIcon, { boxSize: 5 }) })] }), _jsxs(Box, { as: "button", display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.525rem 1rem", border: `1px solid ${primaryColor}`, background: "transparent", borderRadius: "8px", color: textColor, fontSize: "0.875rem", fontWeight: "400", letterSpacing: "1px", cursor: "pointer", transition: "all 0.3s ease", onClick: () => {
                    var _a;
                    if (onEventClick) {
                        onEventClick(events[currentIndex], currentIndex);
                    }
                    else if (((_a = events[currentIndex]) === null || _a === void 0 ? void 0 : _a.uuid) && pubId) {
                        const eventDate = formatDate(events[currentIndex]);
                        const dateString = eventDate ? format(eventDate, "yyyy-MM-dd") : "";
                        navigate(`/pub/detail/events/${pubId}?date=${dateString}&id=${events[currentIndex].uuid}`);
                    }
                }, _hover: { background: "rgba(129, 85, 254, 0.1)" }, _active: { transform: "scale(0.98)" }, "aria-label": appContext === "experience" ? "View Event Details" : "Book Event", children: [appContext === "experience" ? "View Event Details" : "Book Event", _jsx(Icon, { as: FaLongArrowAltRight, boxSize: 3.5 })] })] }));
    // If splitLayout, return components separately wrapped
    if (splitLayout) {
        return (_jsxs(Box, { display: "contents", children: [_jsxs(Box, { "data-carousel-content": true, flex: "1", display: "flex", flexDirection: "column", justifyContent: "space-between", paddingLeft: [0, 0, 0, 8], mt: [0, 0, 0, 24], mb: [0, 0, 0, 10], children: [textContent, navigationControls] }), _jsx(Box, { "data-carousel-image": true, flex: "1", display: "flex", alignItems: "center", justifyContent: "center", children: imageStack }), _jsx("style", { children: `
        [data-card-index] {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        [data-card-index]:focus,
        [data-card-index]:focus-visible,
        [data-card-index]:active,
        [data-card-index]:hover {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        [data-carousel-image] {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        [data-carousel-image]:focus,
        [data-carousel-image]:focus-visible,
        [data-carousel-image]:hover {
          outline: none !important;
        }
        [data-card-index].active {
          pointer-events: auto;
        }
        [data-card-index].dragging {
          transition: none !important;
        }
        [data-card-index].swiping-left,
        [data-card-index].swiping-right {
          transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1),
            opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        [data-content-index].active {
          opacity: 1 !important;
          transform: translateY(0) scale(1) !important;
          pointer-events: auto;
        }
        [data-card-index].active::after {
          content: "";
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(
            45deg,
            rgba(99, 53, 199, 0.3),
            rgba(129, 85, 254, 0.3)
          );
          border-radius: 20px;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          z-index: -1;
        }
        [data-card-index].active:hover::after {
          opacity: 1;
        }
        [data-card-index] img,
        [data-card-index] img:hover,
        [data-card-index] img:focus {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
      ` })] }));
    }
    // Default mobile layout
    return (_jsxs(Box, { position: "relative", borderRadius: "20px", display: "flex", flexDirection: "column", px: 4, children: [imageStack, textContent, navigationControls, _jsx("style", { children: `
        [data-card-index] {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        [data-card-index]:focus,
        [data-card-index]:focus-visible,
        [data-card-index]:active,
        [data-card-index]:hover {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        [data-carousel-image] {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        [data-carousel-image]:focus,
        [data-carousel-image]:focus-visible,
        [data-carousel-image]:hover {
          outline: none !important;
        }
        [data-card-index].active {
          pointer-events: auto;
        }
        [data-card-index].dragging {
          transition: none !important;
        }
        [data-card-index].swiping-left,
        [data-card-index].swiping-right {
          transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1),
            opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        [data-content-index].active {
          opacity: 1 !important;
          transform: translateY(0) scale(1) !important;
          pointer-events: auto;
        }
        [data-card-index].active::after {
          content: "";
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(
            45deg,
            rgba(99, 53, 199, 0.3),
            rgba(129, 85, 254, 0.3)
          );
          border-radius: 20px;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          z-index: -1;
        }
        [data-card-index].active:hover::after {
          opacity: 1;
        }
        [data-card-index] img,
        [data-card-index] img:hover,
        [data-card-index] img:focus {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
      ` })] }));
};
export default UpcomingEventsCarousel;
